import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ChatColumn } from './ChatColumn';

export interface PaperStatusChipProps {
  /** Leading visual: icon, dot, spinner, etc. */
  leading?: ReactNode;
  /** Body text or content. */
  children: ReactNode;
  /** Trailing slot for an action button. */
  action?: ReactNode;
  /** Allow the body to wrap instead of truncating to one line. */
  multiline?: boolean;
  /** Override the outer wrapper class (replaces default). */
  className?: string;
  /** When present, render the chip itself as a button. */
  buttonProps?: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;
}

/**
 * Reusable paper-theme inline status chip used for agent lifecycle messages
 * ("This agent is stopping…", "Setting up your environment…", etc.).
 * Self-contained pill with full rounded border, surface background, hair border.
 */
export const PaperStatusChip = ({
  leading,
  children,
  action,
  multiline = false,
  className,
  buttonProps,
}: PaperStatusChipProps) => {
  const chipClassName = cn(
    'flex flex-wrap items-center gap-x-3 gap-y-1.5',
    'min-h-[40px] py-1.5 px-3 overflow-hidden rounded-xl text-[13px]',
    'border border-solid border-[var(--hair)] bg-[var(--surface)] text-[var(--fg-2)]',
    className,
  );
  const content = (
    <>
      {leading && <span className="shrink-0 flex items-center">{leading}</span>}
      <span
        className={cn(
          'flex-1 min-w-[120px]',
          multiline ? 'whitespace-normal overflow-visible leading-snug' : 'truncate',
        )}
      >
        {children}
      </span>
      {action}
    </>
  );

  if (buttonProps) {
    const { type = 'button', ...restButtonProps } = buttonProps;
    return (
      <button
        type={type}
        {...restButtonProps}
        className={cn(
          'mx-auto w-full max-w-[var(--chat-col-width)] appearance-none font-[inherit] text-left',
          'cursor-pointer transition-colors duration-150 hover:bg-[var(--bg-alt)]',
          'disabled:cursor-default disabled:hover:bg-[var(--surface)]',
          chipClassName,
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <ChatColumn className={chipClassName}>
      {content}
    </ChatColumn>
  );
};
